/**
 * Command Center — Learning library.
 * Benchmarks stored per tenant: closed tests (added
 * automatically) and benchmarks added by hand.
 */

import React, { useEffect, useState } from "react";
import { Modal } from "antd";
import { CcStyles, DeleteModal, ErrorBanner, errMsg } from "./components";
import { CcMode, LibraryEntry, ccApi } from "./api";
import { CHANNELS } from "./common";

const SUB: Record<CcMode, string> = {
  ml: "Every closed test becomes a benchmark. The plan shows the averages for the channel being tested.",
  deck: "Every closed test’s results deck is stored here as a benchmark. Add past partner results by hand to build the baseline.",
};

const EMPTY = { name: "", advertiser: "", channel: "CTV", season: "Q4", weeks: "6", source: "", lift: "", roas: "", met: true, notes: "" };

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
    <span style={{ fontSize: 12.5, fontWeight: 600, color: "var(--text-primary)" }}>{label}</span>
    {children}
  </label>
);

export default function CommandCenterLibrary({ mode }: { mode: CcMode }) {
  const [rows, setRows] = useState<LibraryEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ch, setCh] = useState<string>("all");
  const [season, setSeason] = useState<"all" | "q4">("all");
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ ...EMPTY, source: mode === "ml" ? "Synthetic control" : "Results deck · Adrsta" });
  const [saving, setSaving] = useState(false);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    setRows(null);
    ccApi.library(mode).then(setRows).catch(err => setError(`Failed to load the library: ${errMsg(err)}`));
  }, [mode]);

  const channels = Array.from(new Set((rows || []).map(r => r.channel)));
  const f = (rows || []).filter(r => (ch === "all" || r.channel === ch) && (season === "all" || r.season === "Q4"));
  const withRoas = f.filter(r => r.roas !== null);
  const avg = (vals: number[]) => (vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0);
  const pill = (on: boolean) => `cc-pill${on ? " on" : ""}`;
  const set = (k: keyof typeof EMPTY, v: any) => setForm(p => ({ ...p, [k]: v }));
  const valid = form.name.trim() && form.source.trim() && form.lift !== "" && !isNaN(Number(form.lift)) && Number(form.weeks) > 0
    && (form.roas === "" || !isNaN(Number(form.roas)));

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    setSaving(true);
    try {
      const entry = await ccApi.addLibrary(mode, {
        name: form.name.trim(), advertiser: form.advertiser.trim() || null, channel: form.channel, season: form.season,
        weeks: Number(form.weeks), source: form.source.trim(), lift: Number(form.lift),
        roas: form.roas === "" ? null : Number(form.roas), met: form.met, notes: form.notes.trim() || null,
      });
      setRows(prev => [entry, ...(prev || [])]);
      setAdding(false);
      setForm({ ...EMPTY, source: form.source });
    } catch (err) {
      setError(`Could not add the benchmark: ${errMsg(err)}`);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: string) => {
    setDeleting(true);
    try {
      await ccApi.deleteLibrary(id);
      setRows(prev => (prev || []).filter(r => r.id !== id));
    } catch (err) {
      setError(`Delete failed: ${errMsg(err)}`);
    } finally {
      setDeleting(false);
      setConfirmId(null);
    }
  };
  const rowToDelete = (rows || []).find(r => r.id === confirmId) || null;

  return (
    <div className="cc-page">
      <CcStyles />
      <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "flex-start", gap: 16 }}>
        <div>
          <h1 className="cc-h1">Learning library</h1>
          <p className="cc-sub">{SUB[mode]}</p>
        </div>
        <button className="cc-btn primary" onClick={() => setAdding(true)}>+ Add benchmark</button>
      </div>

      <ErrorBanner msg={error} onClose={() => setError(null)} />

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <span style={{ fontSize: 12.5, color: "var(--text-tertiary)", marginRight: 4 }}>Channel</span>
        <button className={pill(ch === "all")} onClick={() => setCh("all")}>All</button>
        {channels.map(c => <button key={c} className={pill(ch === c)} onClick={() => setCh(c)}>{c}</button>)}
        <span style={{ fontSize: 12.5, color: "var(--text-tertiary)", margin: "0 4px 0 14px" }}>Season</span>
        <button className={pill(season === "all")} onClick={() => setSeason("all")}>Any</button>
        <button className={pill(season === "q4")} onClick={() => setSeason("q4")}>Q4 only</button>
      </div>

      <div className="cc-grid-4">
        <div className="cc-card" style={{ gap: 4 }}><span className="cc-lbl">Tests</span><span className="cc-val">{rows ? f.length : "–"}</span></div>
        <div className="cc-card" style={{ gap: 4 }}><span className="cc-lbl">Avg lift</span><span className="cc-val">{f.length ? (avg(f.map(r => r.lift)) >= 0 ? "+" : "") + avg(f.map(r => r.lift)).toFixed(1) + "%" : "—"}</span></div>
        <div className="cc-card" style={{ gap: 4 }}><span className="cc-lbl">Avg iROAS</span><span className="cc-val">{withRoas.length ? avg(withRoas.map(r => r.roas as number)).toFixed(1) + "x" : "—"}</span></div>
        <div className="cc-card" style={{ gap: 4 }}><span className="cc-lbl">Met their bar</span><span className="cc-val">{f.length ? `${f.filter(r => r.met).length} of ${f.length}` : "—"}</span></div>
      </div>

      {!rows ? (
        !error && <div className="cc-grid-3">{[1, 2, 3].map(i => <div key={i} className="cc-skel" style={{ height: 140 }} />)}</div>
      ) : f.length === 0 ? (
        <div className="cc-card"><span className="cc-help">No benchmarks match these filters.</span></div>
      ) : (
        <div className="cc-grid-3">
          {f.map(r => (
            <div key={r.id} className="cc-card" style={{ gap: 10 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "flex-start" }}>
                <span style={{ fontWeight: 600 }}>{r.advertiser ? `${r.advertiser} · ` : ""}{r.name}</span>
                <span style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <span style={{ fontSize: 11, color: r.met ? "var(--success-card)" : "var(--warning)", whiteSpace: "nowrap" }}>{r.met ? "Bar met" : "Bar missed"}</span>
                  <button className="cc-icon-btn" title="Delete benchmark" aria-label="Delete benchmark" onClick={() => setConfirmId(r.id)}>✕</button>
                </span>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                <span className="cc-chip">{r.channel}</span><span className="cc-chip">{r.season}</span><span className="cc-chip">{r.weeks} wk</span><span className="cc-chip">{r.source}</span>
                {r.test_id && <span className="cc-chip" style={{ color: "var(--blue)", background: "var(--blue-subtle)" }}>From a closed test</span>}
                {!r.test_id && !r.is_default && <span className="cc-chip" style={{ color: "var(--violet)", background: "var(--violet-subtle)" }}>Added by hand</span>}
              </div>
              <div style={{ display: "flex", gap: 20, borderTop: "1px solid var(--border-subtle)", paddingTop: 10 }}>
                <div><div className="cc-lbl">Lift</div><div className="cc-val" style={{ color: r.lift >= 0 ? "var(--success-card)" : "var(--error)" }}>{r.lift >= 0 ? "+" : ""}{r.lift.toFixed(1)}%</div></div>
                <div><div className="cc-lbl">iROAS</div><div className="cc-val">{r.roas !== null ? r.roas.toFixed(1) + "x" : "—"}</div></div>
              </div>
              {r.notes && <span className="cc-help">{r.notes}</span>}
            </div>
          ))}
        </div>
      )}

      <Modal open={adding} onCancel={() => setAdding(false)} footer={null} width={560} destroyOnHidden centered maskClosable={false}>
        <form onSubmit={save} style={{ paddingTop: 4, fontFamily: "var(--font-sans)" }}>
          <div style={{ marginBottom: 18, paddingRight: 24 }}>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: "var(--text-primary)" }}>Add a benchmark</h2>
            <p style={{ margin: "4px 0 0", fontSize: 13, color: "var(--text-secondary)" }}>A past geo test result. It is used in plan benchmarks for the same channel.</p>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 14, maxHeight: "62vh", overflowY: "auto", paddingRight: 4 }}>
            <div className="cc-grid-2">
              <Field label="Test name"><input className="cc-modal-input" value={form.name} onChange={e => set("name", e.target.value)} placeholder="e.g. Holiday 2025" required /></Field>
              <Field label="Advertiser (optional)"><input className="cc-modal-input" value={form.advertiser} onChange={e => set("advertiser", e.target.value)} placeholder="e.g. Acme Home" /></Field>
            </div>
            <div className="cc-grid-3">
              <Field label="Channel">
                <select className="cc-modal-input" value={form.channel} onChange={e => set("channel", e.target.value)}>{CHANNELS.map(c => <option key={c}>{c}</option>)}</select>
              </Field>
              <Field label="Season">
                <select className="cc-modal-input" value={form.season} onChange={e => set("season", e.target.value)}>{["Q1", "Q2", "Q3", "Q4"].map(q => <option key={q}>{q}</option>)}</select>
              </Field>
              <Field label="Weeks"><input className="cc-modal-input" type="number" min={1} max={52} value={form.weeks} onChange={e => set("weeks", e.target.value)} /></Field>
            </div>
            <Field label="Method / source"><input className="cc-modal-input" value={form.source} onChange={e => set("source", e.target.value)} placeholder="e.g. Synthetic control, RCT 50/50, Results deck · Partner" /></Field>
            <div className="cc-grid-3">
              <Field label="Lift (%)"><input className="cc-modal-input" type="number" step="0.1" value={form.lift} onChange={e => set("lift", e.target.value)} placeholder="5.2" required /></Field>
              <Field label="iROAS (x, optional)"><input className="cc-modal-input" type="number" step="0.1" min={0} value={form.roas} onChange={e => set("roas", e.target.value)} placeholder="2.1" /></Field>
              <Field label="Met its bar?">
                <select className="cc-modal-input" value={form.met ? "yes" : "no"} onChange={e => set("met", e.target.value === "yes")}><option value="yes">Yes</option><option value="no">No</option></select>
              </Field>
            </div>
            <Field label="Notes (optional)"><textarea className="cc-modal-input" rows={2} value={form.notes} onChange={e => set("notes", e.target.value)} /></Field>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, borderTop: "1px solid var(--border-subtle)", paddingTop: 14, marginTop: 18 }}>
            <button type="button" className="cc-btn" onClick={() => setAdding(false)}>Cancel</button>
            <button type="submit" className={`cc-btn${valid ? " primary" : ""}`} disabled={!valid || saving}>{saving ? "Saving…" : "Add benchmark"}</button>
          </div>
        </form>
      </Modal>
      {rowToDelete && (
        <DeleteModal name={`${rowToDelete.advertiser ? rowToDelete.advertiser + " · " : ""}${rowToDelete.name}`} loading={deleting}
          body="This benchmark will be removed from the Learning library and from future plan benchmarks. The test itself is not deleted."
          onConfirm={() => remove(rowToDelete.id)} onCancel={() => setConfirmId(null)} />
      )}
    </div>
  );
}
